/**
 * Equipment Service Calculator
 *
 * Calculates equipment service status from job completions.
 * Primary source: job.completions[] (post-BUG-005 pattern)
 *
 * This calculator is DEPRECATED for new code - FT-142 moved service status
 * to the equipment record itself (item.lastServiced, item.nextServiceDue).
 * Kept for backwards compatibility during migration.
 *
 * See DOMAIN_SPINE.md Section 5: Maintenance Completion Tracking Pattern
 */

/**
 * Calculate equipment service status from job completions
 * Returns equipment with computed display fields prefixed with _
 *
 * @param {Array} equipment - Equipment items from register
 * @param {Array} maintenanceJobs - Jobs with completions[] arrays
 * @returns {Array} - Equipment with computed _lastServiceDate, _lastServiceBy, etc.
 */
export function calculateEquipmentServiceStatus(equipment, maintenanceJobs) {
  if (!equipment || !Array.isArray(equipment)) return equipment || [];
  if (!maintenanceJobs || !Array.isArray(maintenanceJobs)) return equipment;

  return equipment.map(eq => {
    // Find all jobs linked to this equipment
    const linkedJobs = maintenanceJobs.filter(job =>
      job.linkedEquipmentIds?.includes(eq.id)
    );

    // Find latest completion across ALL jobs for this equipment
    let latestCompletion = null;
    let totalCompletions = 0;
    let earliestNextDue = null;

    linkedJobs.forEach(job => {
      const equipmentCompletions = (job.completions || [])
        .filter(c => c.equipmentIds?.includes(eq.id));

      totalCompletions += equipmentCompletions.length;

      if (equipmentCompletions.length > 0) {
        const latest = equipmentCompletions
          .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];

        if (!latestCompletion ||
            new Date(latest.completedAt) > new Date(latestCompletion.completedAt)) {
          latestCompletion = latest;
        }

        // Track earliest nextDue across all completions for this equipment
        if (latest.nextDue) {
          const nextDueDate = new Date(latest.nextDue);
          if (!earliestNextDue || nextDueDate < new Date(earliestNextDue)) {
            earliestNextDue = latest.nextDue;
          }
        }
      }
    });

    return {
      ...eq,
      // Computed display fields (prefixed with _ to indicate non-stored)
      _lastServiceDate: latestCompletion?.completedAt || null,
      _lastServiceBy: latestCompletion?.performedBy || null,
      _serviceCount: totalCompletions,
      _hasBeenServiced: totalCompletions > 0,
      _nextServiceDue: earliestNextDue,
      _linkedJobCount: linkedJobs.length
    };
  });
}

/**
 * Get formatted last service text for equipment display
 *
 * @param {Object} equipment - Equipment with computed fields
 * @returns {string} - Formatted date string or 'Never'
 */
export function getEquipmentLastServiceText(equipment) {
  if (!equipment?._lastServiceDate) return 'Never';

  const date = new Date(equipment._lastServiceDate);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Get formatted next service due text for equipment display
 *
 * @param {Object} equipment - Equipment with computed fields
 * @returns {string} - Formatted date string or 'Not scheduled'
 */
export function getEquipmentNextDueText(equipment) {
  if (!equipment?._nextServiceDue) return 'Not scheduled';

  const date = new Date(equipment._nextServiceDue);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Calculate service status for a single equipment item
 * Useful when you have a single item and all jobs
 *
 * @param {Object} equipmentItem - Single equipment item
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @returns {Object} - Equipment item with computed fields
 */
export function calculateSingleEquipmentServiceStatus(equipmentItem, maintenanceJobs) {
  const result = calculateEquipmentServiceStatus([equipmentItem], maintenanceJobs);
  return result[0];
}
